import {
  isReady,
  trackConversion,
  type EventProperties,
} from "@/utils/posthog";

const PENDING_KIT_CONVERSION_KEY =
  "technical-leaders:pending-kit-conversion:v1";
const PENDING_KIT_CONVERSION_MAX_AGE_MS = 10 * 60 * 1000;

type PendingKitConversion = {
  conversionName: string;
  properties: EventProperties;
  createdAt: number;
  confirmationPath?: string;
};

export type KitConversionFlushResult =
  | "none"
  | "waiting_for_path"
  | "waiting_for_posthog"
  | "flushed";

const clearPendingKitConversion = () => {
  try {
    window.sessionStorage.removeItem(PENDING_KIT_CONVERSION_KEY);
  } catch {
    // Storage can be unavailable in restricted browser contexts.
  }
};

export const queueKitConversion = (
  conversionName: string,
  properties: EventProperties,
  confirmationPath?: string,
) => {
  const pendingConversion: PendingKitConversion = {
    conversionName,
    properties,
    createdAt: Date.now(),
    confirmationPath,
  };

  try {
    window.sessionStorage.setItem(
      PENDING_KIT_CONVERSION_KEY,
      JSON.stringify(pendingConversion),
    );
  } catch {
    // Fall back to an immediate best-effort send if storage is unavailable.
    if (isReady()) {
      trackConversion(conversionName, properties);
    }
  }
};

export const flushPendingKitConversion = (
  currentPath = window.location.pathname,
): KitConversionFlushResult => {
  let pendingConversion: PendingKitConversion;

  try {
    const rawPendingConversion = window.sessionStorage.getItem(
      PENDING_KIT_CONVERSION_KEY,
    );
    if (!rawPendingConversion) return "none";

    pendingConversion = JSON.parse(rawPendingConversion) as PendingKitConversion;
  } catch {
    clearPendingKitConversion();
    return "none";
  }

  if (
    !pendingConversion.conversionName ||
    !pendingConversion.createdAt ||
    Date.now() - pendingConversion.createdAt >
      PENDING_KIT_CONVERSION_MAX_AGE_MS
  ) {
    clearPendingKitConversion();
    return "none";
  }

  if (
    pendingConversion.confirmationPath &&
    pendingConversion.confirmationPath !== currentPath
  ) {
    return "waiting_for_path";
  }

  if (!isReady()) return "waiting_for_posthog";

  trackConversion(
    pendingConversion.conversionName,
    pendingConversion.properties,
  );
  clearPendingKitConversion();
  return "flushed";
};
